import { useState } from 'react';
import type { Lead, Turma } from '@/mocks/admin';

interface LeadDetalheModalProps {
  lead: Lead;
  turmas: Turma[];
  onClose: () => void;
  onStatusChange: (leadId: string, status: Lead['status']) => void;
  onTurmaChange: (leadId: string, turmaId: string | null) => void;
  onUpdate?: (leadId: string, data: Partial<Lead>) => void;
  defaultTab?: Tab;
}

type Tab = 'perfil' | 'diagnostico' | 'dados';

const statusConfig: Record<Lead['status'], { label: string; color: string; bg: string; border: string; dot: string }> = {
  novo:       { label: 'Novo',       color: 'text-gray-600',   bg: 'bg-gray-100',   border: 'border-gray-300',   dot: 'bg-gray-400' },
  contatado:  { label: 'Contatado',  color: 'text-amber-700',  bg: 'bg-amber-50',   border: 'border-amber-300',  dot: 'bg-amber-500' },
  interessado:{ label: 'Interessado TR60D', color: 'text-amber-700',  bg: 'bg-amber-50',   border: 'border-amber-300',  dot: 'bg-amber-500' },
  confirmado: { label: 'Confirmado', color: 'text-emerald-700',bg: 'bg-emerald-50', border: 'border-emerald-300',dot: 'bg-emerald-500' },
  desistiu:   { label: 'Desistiu',   color: 'text-red-600',    bg: 'bg-red-50',     border: 'border-red-300',    dot: 'bg-red-500' },
};

const origemConfig: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  diagnostico: { label: 'Diagnóstico', icon: 'ri-bar-chart-2-line', color: 'text-violet-600', bg: 'bg-violet-50' },
  programa:    { label: 'Programa',    icon: 'ri-graduation-cap-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

const nivelConfig: Record<number, {
  titulo: string; subtitulo: string; mensagem: string; problemas: string[];
  gradFrom: string; gradTo: string; textColor: string; badgeBg: string;
  badgeText: string; borderColor: string; bgCard: string; emoji: string; posicao: number;
}> = {
  1: {
    titulo: 'Negócio em Risco', subtitulo: 'Alta desorganização e dependência total do dono.',
    mensagem: 'Seu negócio possui gargalos críticos que limitam crescimento.',
    problemas: [
      'Fluxo de caixa descontrolado gerando crises constantes',
      'Vendas imprevisíveis sem processo definido',
      'Exposição jurídica elevada — sem contratos ou documentos organizados',
      'Tudo depende de você — sem delegação real',
    ],
    gradFrom: 'from-red-600', gradTo: 'to-red-800',
    textColor: 'text-red-600', badgeBg: 'bg-red-100', badgeText: 'text-red-700',
    borderColor: 'border-red-200', bgCard: 'bg-red-50', emoji: '🚨', posicao: 12,
  },
  2: {
    titulo: 'Negócio Desorganizado', subtitulo: 'Algumas áreas funcionam, mas sem estrutura clara.',
    mensagem: 'Você tem potencial de crescimento, mas falta estrutura.',
    problemas: [
      'Processos existem na cabeça, não no papel',
      'Equipe sem direção clara e retrabalho constante',
      'Contratos e documentos improvisados — risco jurídico latente',
      'Decisões tomadas no achismo sem dados concretos',
    ],
    gradFrom: 'from-orange-500', gradTo: 'to-orange-700',
    textColor: 'text-orange-600', badgeBg: 'bg-orange-100', badgeText: 'text-orange-700',
    borderColor: 'border-orange-200', bgCard: 'bg-orange-50', emoji: '⚠️', posicao: 37,
  },
  3: {
    titulo: 'Negócio Estruturando', subtitulo: 'Já existe controle em algumas áreas.',
    mensagem: 'Seu negócio está no caminho certo, mas ainda existem gargalos.',
    problemas: [
      'Falta padronização para escalar sem perder qualidade',
      'Indicadores parciais dificultam decisões estratégicas',
      'Proteção jurídica parcial — ainda existem vulnerabilidades',
      'Automação ainda não está sendo aproveitada ao máximo',
    ],
    gradFrom: 'from-amber-500', gradTo: 'to-amber-700',
    textColor: 'text-amber-600', badgeBg: 'bg-amber-100', badgeText: 'text-amber-700',
    borderColor: 'border-amber-200', bgCard: 'bg-amber-50', emoji: '📈', posicao: 62,
  },
  4: {
    titulo: 'Pronto para Escalar', subtitulo: 'Estrutura avançada com base sólida.',
    mensagem: 'Seu negócio possui boa base para crescimento acelerado.',
    problemas: [
      'Otimizar processos para suportar crescimento acelerado',
      'Implementar IA e automação para ganhar vantagem competitiva',
      'Blindagem jurídica completa para expansão segura',
      'Estruturar liderança para expansão sem depender do dono',
    ],
    gradFrom: 'from-emerald-500', gradTo: 'to-emerald-700',
    textColor: 'text-emerald-600', badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-700',
    borderColor: 'border-emerald-200', bgCard: 'bg-emerald-50', emoji: '🚀', posicao: 88,
  },
};

const areaConfig: Record<string, {
  label: string; icon: string; cor: string; corBg: string; corText: string; corBar: string;
  diagnosticos: string[]; recomendacoes: string[];
}> = {
  financeiro: {
    label: 'Controle Financeiro', icon: 'ri-money-dollar-circle-line',
    cor: 'bg-emerald-500', corBg: 'bg-emerald-50', corText: 'text-emerald-700', corBar: 'bg-emerald-500',
    diagnosticos: ['Sem controle financeiro — alto risco de crise de caixa','Controle informal — decisões baseadas em estimativas','Controle básico — falta visibilidade para crescer','Controle sólido — base para escalar com segurança'],
    recomendacoes: ['Implante urgentemente um fluxo de caixa simples e separe finanças pessoais das empresariais.','Estruture um DRE simplificado e comece a registrar todas as entradas e saídas.','Avance para indicadores como margem de contribuição e ponto de equilíbrio.','Otimize com dashboard financeiro e automatize relatórios para decisões mais rápidas.'],
  },
  vendas: {
    label: 'Processo de Vendas', icon: 'ri-hand-coin-line',
    cor: 'bg-orange-500', corBg: 'bg-orange-50', corText: 'text-orange-700', corBar: 'bg-orange-500',
    diagnosticos: ['Vendas no improviso — receita imprevisível e instável','Processo informal — depende do feeling de cada vendedor','Processo parcial — falta padronização e métricas','Processo estruturado — vendas previsíveis e escaláveis'],
    recomendacoes: ['Crie um processo básico de vendas: prospecção, abordagem, proposta e fechamento.','Documente o processo atual e defina etapas claras para cada venda.','Implante um CRM simples e comece a medir taxa de conversão.','Refine scripts, automatize follow-ups e expanda o time com processo replicável.'],
  },
  gestao: {
    label: 'Gestão e Processos', icon: 'ri-settings-3-line',
    cor: 'bg-sky-500', corBg: 'bg-sky-50', corText: 'text-sky-700', corBar: 'bg-sky-500',
    diagnosticos: ['Sem processos — operação caótica e dependente do dono','Processos informais — conhecimento concentrado em pessoas','Processos parciais — falta documentação e padronização','Processos definidos — operação previsível e delegável'],
    recomendacoes: ['Mapeie as 3 atividades mais críticas e crie um passo a passo simples para cada.','Documente os processos existentes e defina responsáveis claros.','Crie um manual operacional e implante rotinas de acompanhamento.','Automatize processos repetitivos e foque em melhoria contínua.'],
  },
  indicadores: {
    label: 'Indicadores', icon: 'ri-bar-chart-2-line',
    cor: 'bg-violet-500', corBg: 'bg-violet-50', corText: 'text-violet-700', corBar: 'bg-violet-500',
    diagnosticos: ['Sem indicadores — decisões no escuro','Apenas faturamento — visão limitada do negócio','Indicadores parciais — falta visão integrada','Painel completo — gestão orientada por dados'],
    recomendacoes: ['Comece com 3 indicadores: faturamento, ticket médio e número de clientes.','Adicione indicadores de custo, margem e inadimplência.','Crie um painel semanal com os principais KPIs do negócio.','Implante BI ou dashboard automatizado para decisões em tempo real.'],
  },
  dependencia: {
    label: 'Dependência do Dono', icon: 'ri-user-star-line',
    cor: 'bg-red-500', corBg: 'bg-red-50', corText: 'text-red-700', corBar: 'bg-red-500',
    diagnosticos: ['Total dependência — negócio para sem o dono','Alta dependência — operação instável sem o dono','Dependência parcial — alguns processos funcionam sozinhos','Baixa dependência — negócio funciona de forma autônoma'],
    recomendacoes: ['Identifique as 5 tarefas que só você faz e comece a documentar e delegar uma por vez.','Treine um colaborador para assumir responsabilidades operacionais.','Crie um manual de operações e implante reuniões de acompanhamento.','Foque em liderança estratégica e deixe a operação rodar sem sua presença diária.'],
  },
  tecnologia: {
    label: 'Tecnologia e IA', icon: 'ri-robot-2-line',
    cor: 'bg-rose-500', corBg: 'bg-rose-50', corText: 'text-rose-700', corBar: 'bg-rose-500',
    diagnosticos: ['Sem tecnologia — operação manual e lenta','Tecnologia básica — potencial desperdiçado','Automações parciais — ganhos limitados','Tecnologia avançada — vantagem competitiva real'],
    recomendacoes: ['Comece com ferramentas gratuitas: Google Workspace, Trello e WhatsApp Business.','Implante um CRM e automatize o envio de mensagens e follow-ups.','Use IA para criar conteúdo, responder clientes e analisar dados.','Integre sistemas e crie fluxos de automação para ganhar escala sem aumentar equipe.'],
  },
  time_comercial: {
    label: 'Time Comercial', icon: 'ri-team-line',
    cor: 'bg-amber-500', corBg: 'bg-amber-50', corText: 'text-amber-700', corBar: 'bg-amber-500',
    diagnosticos: ['Sem estrutura comercial — vendas dependem do acaso','Estrutura inicial — falta processo e previsibilidade','Estrutura parcial — time existe mas sem rotina clara','Estrutura sólida — time autônomo e orientado a resultados'],
    recomendacoes: ['Defina metas claras e crie um processo básico de prospecção e atendimento.','Estruture uma rotina comercial diária com metas, atividades e acompanhamento.','Implante reuniões de pipeline e acompanhe conversão por etapa do funil.','Automatize relatórios e foque em recrutamento e treinamento para expansão.'],
  },
  juridico: {
    label: 'Maturidade Jurídica', icon: 'ri-scales-3-line',
    cor: 'bg-indigo-600', corBg: 'bg-indigo-50', corText: 'text-indigo-700', corBar: 'bg-indigo-600',
    diagnosticos: ['Exposição jurídica crítica — empresa vulnerável a conflitos','Baixa proteção jurídica — documentos improvisados e dispersos','Proteção parcial — organização existe mas com lacunas','Proteção jurídica sólida — empresa blindada e organizada'],
    recomendacoes: ['Formalize urgentemente contratos com clientes, fornecedores e colaboradores.','Centralize todos os documentos em uma pasta digital organizada por categoria.','Padronize contratos e crie uma biblioteca de documentos de fácil acesso.','Implante uma plataforma jurídica empresarial e revise contratos periodicamente.'],
  },
};

function GaugeMeter({ posicao }: { posicao: number }) {
  const cx = 140; const cy = 130; const r = 100;
  const angle = -180 + (posicao / 100) * 180;
  const rad = (angle * Math.PI) / 180;
  const px = cx + r * Math.cos(rad);
  const py = cy + r * Math.sin(rad);
  return (
    <div style={{ width: 200, height: 115 }} className="relative mx-auto">
      <svg viewBox="0 0 280 170" className="w-full h-full overflow-visible">
        <defs>
          <linearGradient id="gaugeGradLead" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="45%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="20" strokeLinecap="round" />
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="url(#gaugeGradLead)" strokeWidth="20" strokeLinecap="round" />
        <line x1={cx} y1={cy} x2={px} y2={py} stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="10" fill="#f59e0b" />
        <circle cx={cx} cy={cy} r="5" fill="#374151" />
        <text x="18" y="165" fill="#9ca3af" fontSize="10" fontWeight="700" textAnchor="middle">Risco</text>
        <text x="262" y="165" fill="#9ca3af" fontSize="10" fontWeight="700" textAnchor="middle">Escala</text>
      </svg>
    </div>
  );
}

function getAreaRecomendacao(areaKey: string, pontos: number, maxPontos: number): string {
  const cfg = areaConfig[areaKey];
  if (!cfg) return '—';
  const pct = maxPontos > 0 ? pontos / maxPontos : 0;
  const idx = pct <= 0.25 ? 0 : pct <= 0.5 ? 1 : pct <= 0.75 ? 2 : 3;
  return cfg.recomendacoes[idx];
}

export default function LeadDetalheModal({ lead, turmas, onClose, onStatusChange, onTurmaChange, onUpdate, defaultTab }: LeadDetalheModalProps) {
  const [tab, setTab] = useState<Tab>(defaultTab || 'perfil');

  const statusCfg = statusConfig[lead.status];
  const turmaAtual = turmas.find((t) => t.id === lead.turmaId);
  const nivelCfg = lead.diagnostico ? nivelConfig[lead.diagnostico.nivelNumero] : null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const formatarData = (iso: string) =>
    new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden shadow-xl">

        {/* ── HEADER CLEAN ── */}
        <div className="px-6 pt-6 pb-0 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="relative">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-amber-100 flex-shrink-0">
                  <span className="text-amber-700 font-black text-xl">{lead.nome.charAt(0)}</span>
                </div>
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${statusCfg.dot}`} />
              </div>
              <div>
                <h2 className="text-gray-900 font-black text-lg leading-tight">{lead.nome}</h2>
                <p className="text-gray-400 text-sm">{lead.empresa}</p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-0.5 rounded-full border ${statusCfg.bg} ${statusCfg.color} ${statusCfg.border}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                    {statusCfg.label}
                  </span>
                  {lead.origens.map((origem) => {
                    const cfg = origemConfig[origem] ?? origemConfig['diagnostico'];
                    return (
                      <span
                        key={origem}
                        className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}
                      >
                        <i className={`${cfg.icon} text-xs`} />
                        {cfg.label}
                      </span>
                    );
                  })}
                  {lead.diagnostico && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-100 px-2.5 py-0.5 rounded-full">
                      N{lead.diagnostico.nivelNumero} · {lead.diagnostico.percentualGeral}%
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-gray-400 text-lg" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1">
            {([
              { key: 'perfil', label: 'Perfil', icon: 'ri-user-3-line' },
              { key: 'diagnostico', label: 'Diagnóstico', icon: 'ri-bar-chart-2-line', disabled: !lead.diagnostico },
              { key: 'dados', label: 'Dados', icon: 'ri-file-edit-line' },
            ] as { key: Tab; label: string; icon: string; disabled?: boolean }[]).map((t) => (
              <button
                key={t.key}
                onClick={() => !t.disabled && setTab(t.key)}
                disabled={t.disabled}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold transition-all whitespace-nowrap border-b-2 -mb-px ${
                  tab === t.key
                    ? 'text-gray-900 border-gray-900'
                    : t.disabled
                      ? 'text-gray-300 border-transparent cursor-not-allowed'
                      : 'text-gray-400 border-transparent hover:text-gray-600 cursor-pointer'
                }`}
              >
                <i className={`${t.icon} text-sm`} />
                {t.label}
                {t.key === 'diagnostico' && !lead.diagnostico && (
                  <span className="text-xs text-gray-300 font-normal">(sem dados)</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── CONTEÚDO ── */}
        <div className="overflow-y-auto flex-1">

          {/* ── TAB: PERFIL ── */}
          {tab === 'perfil' && (
            <div className="p-6 space-y-6">

              {/* Contato */}
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3">Informações de contato</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {[
                    { icon: 'ri-mail-line', label: 'E-mail', value: lead.email, color: 'text-violet-500' },
                    { icon: 'ri-whatsapp-line', label: 'WhatsApp', value: lead.whatsapp, color: 'text-emerald-500' },
                    { icon: 'ri-building-2-line', label: 'Empresa', value: lead.empresa, color: 'text-sky-500' },
                    { icon: 'ri-calendar-event-line', label: 'Cadastro', value: formatarData(lead.dataCadastro), color: 'text-amber-500' },
                    { icon: 'ri-briefcase-line', label: 'Segmento', value: lead.segmento || '—', color: 'text-orange-500' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3.5 border border-gray-100">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100 flex-shrink-0">
                        <i className={`${item.icon} ${item.color} text-sm`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-gray-400 text-xs">{item.label}</p>
                        <p className="text-gray-800 text-sm font-semibold truncate">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Maior desafio */}
              {lead.maior_desafio && (
                <div>
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3">Maior desafio</p>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-gray-700 text-sm leading-relaxed">{lead.maior_desafio}</p>
                  </div>
                </div>
              )}

              {/* Ações rápidas */}
              <div className="flex gap-2">
                <a
                  href={`mailto:${lead.email}`}
                  className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line" />
                  Enviar e-mail
                </a>
                {lead.whatsapp && (
                  <a
                    href={`https://wa.me/55${lead.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white text-xs font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line" />
                    Abrir WhatsApp
                  </a>
                )}
              </div>

              {/* Status */}
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3">Status do lead</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(Object.keys(statusConfig) as Lead['status'][]).map((s) => {
                    const cfg = statusConfig[s];
                    const isActive = lead.status === s;
                    return (
                      <button
                        key={s}
                        onClick={() => onStatusChange(lead.id, s)}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                          isActive
                            ? `${cfg.bg} ${cfg.border}`
                            : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                        <span className={`text-xs font-black ${isActive ? cfg.color : 'text-gray-400'}`}>{cfg.label}</span>
                        {isActive && <i className={`ri-check-line text-xs ${cfg.color}`} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Turma */}
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3">Turma</p>
                <div className="space-y-2">
                  <button
                    onClick={() => onTurmaChange(lead.id, null)}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                      !lead.turmaId
                        ? 'bg-gray-100 border-gray-300'
                        : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${!lead.turmaId ? 'bg-gray-300' : 'bg-gray-200'}`}>
                      <i className="ri-close-circle-line text-sm text-gray-500" />
                    </div>
                    <span className={`text-sm font-bold ${!lead.turmaId ? 'text-gray-700' : 'text-gray-400'}`}>Sem turma</span>
                    {!lead.turmaId && (
                      <div className="ml-auto w-5 h-5 flex items-center justify-center rounded-full bg-gray-500">
                        <i className="ri-check-line text-white text-xs" />
                      </div>
                    )}
                  </button>

                  {turmas.filter((t) => t.status !== 'encerrada').map((t) => {
                    const isActive = lead.turmaId === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => onTurmaChange(lead.id, t.id)}
                        className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                          isActive
                            ? 'bg-amber-50 border-amber-300'
                            : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${isActive ? 'bg-amber-500' : 'bg-gray-200'}`}>
                          <i className={`ri-calendar-2-line text-sm ${isActive ? 'text-white' : 'text-gray-500'}`} />
                        </div>
                        <span className={`text-sm font-bold flex-1 ${isActive ? 'text-amber-800' : 'text-gray-700'}`}>{t.nome}</span>
                        {isActive && (
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-500 flex-shrink-0">
                            <i className="ri-check-line text-white text-xs" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── TAB: DIAGNÓSTICO ── */}
          {tab === 'diagnostico' && lead.diagnostico && nivelCfg && (
            <div className="p-6 space-y-5">

              {/* Card do nível — colorido mas não dark */}
              <div className={`${nivelCfg.bgCard} border-2 ${nivelCfg.borderColor} rounded-2xl p-5 overflow-hidden relative`}>
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="flex-1 text-center sm:text-left">
                    <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                      <span className="text-3xl">{nivelCfg.emoji}</span>
                      <span className={`text-xs font-bold uppercase tracking-widest ${nivelCfg.textColor}`}>Nível {lead.diagnostico.nivelNumero}</span>
                    </div>
                    <p className={`font-black text-2xl leading-tight mb-1 ${nivelCfg.textColor}`}>{nivelCfg.titulo}</p>
                    <p className="text-gray-500 text-sm mb-4">{nivelCfg.subtitulo}</p>
                    <div className="flex items-center gap-3">
                      <div className="bg-white rounded-xl px-4 py-2.5 text-center border border-white">
                        <p className="text-gray-400 text-xs">Pontuação</p>
                        <p className={`font-black text-xl ${nivelCfg.textColor}`}>
                          {lead.diagnostico.pontuacaoTotal}
                          <span className="text-gray-400 text-sm font-normal">/{lead.diagnostico.pontuacaoMaxima}</span>
                        </p>
                      </div>
                      <div className="bg-white rounded-xl px-4 py-2.5 text-center border border-white">
                        <p className="text-gray-400 text-xs">Percentual</p>
                        <p className={`font-black text-xl ${nivelCfg.textColor}`}>{lead.diagnostico.percentualGeral}%</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <GaugeMeter posicao={nivelCfg.posicao} />
                  </div>
                </div>

                {/* Problemas */}
                <div className="mt-4 pt-4 border-t border-black/10">
                  <p className={`text-xs font-bold italic ${nivelCfg.textColor} mb-3`}>&ldquo;{nivelCfg.mensagem}&rdquo;</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {nivelCfg.problemas.map((prob) => (
                      <div key={prob} className="flex items-start gap-2">
                        <div className={`w-4 h-4 flex items-center justify-center rounded-full ${nivelCfg.badgeBg} flex-shrink-0 mt-0.5`}>
                          <i className={`ri-alert-line ${nivelCfg.textColor} text-xs`} />
                        </div>
                        <p className="text-gray-600 text-xs leading-relaxed">{prob}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Resultado por área */}
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3">Resultado por área</p>
                <div className="space-y-3">
                  {lead.diagnostico.areas.map((area) => {
                    const cfg = areaConfig[area.areaKey];
                    if (!cfg) return null;
                    const recomendacao = getAreaRecomendacao(area.areaKey, area.pontos, area.maxPontos);
                    const pct = area.maxPontos > 0 ? Math.round((area.pontos / area.maxPontos) * 100) : 0;

                    return (
                      <div key={area.areaKey} className={`${cfg.corBg} rounded-2xl p-4 border border-white`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${cfg.cor}`}>
                              <i className={`${cfg.icon} text-white text-sm`} />
                            </div>
                            <span className={`font-black text-sm ${cfg.corText}`}>{cfg.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">{area.pontos}/{area.maxPontos} pts</span>
                            <span className={`text-sm font-black ${cfg.corText}`}>{pct}%</span>
                          </div>
                        </div>
                        <div className="w-full h-2 bg-white/70 rounded-full overflow-hidden mb-3">
                          <div
                            className={`h-full rounded-full ${cfg.corBar} transition-all duration-700`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className={`text-xs font-bold ${cfg.corText} mb-2`}>{area.diagnostico}</p>
                        <div className="bg-white/70 rounded-xl p-3">
                          <div className="flex items-start gap-2">
                            <div className={`w-5 h-5 flex items-center justify-center rounded-lg ${cfg.cor} flex-shrink-0 mt-0.5`}>
                              <i className="ri-lightbulb-line text-white text-xs" />
                            </div>
                            <p className="text-gray-600 text-xs leading-relaxed">
                              <span className="font-bold text-gray-700">Recomendação: </span>
                              {recomendacao}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Sem diagnóstico */}
          {tab === 'diagnostico' && !lead.diagnostico && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                <i className="ri-bar-chart-2-line text-gray-300 text-3xl" />
              </div>
              <p className="text-gray-500 font-bold mb-1">Sem diagnóstico</p>
              <p className="text-gray-400 text-sm">Este lead não realizou o diagnóstico ainda.</p>
            </div>
          )}

          {/* ── TAB: DADOS ── */}
          {tab === 'dados' && (
            <DadosForm lead={lead} onUpdate={onUpdate} />
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white">
          <button
            onClick={onClose}
            className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function DadosForm({ lead, onUpdate }: { lead: Lead; onUpdate?: (leadId: string, data: Partial<Lead>) => void }) {
  const [email, setEmail] = useState(lead.email || '');
  const [porte, setPorte] = useState(lead.porte || '');
  const [tempo, setTempo] = useState(lead.tempo_mercado || '');
  const [colaboradores, setColaboradores] = useState(lead.num_colaboradores || '');
  const [faturamento, setFaturamento] = useState(lead.faturamento || '');
  const [desafio, setDesafio] = useState(lead.desafio || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    if (!onUpdate) return;
    setSaving(true);
    onUpdate(lead.id, { email, porte, tempo_mercado: tempo, num_colaboradores: colaboradores, faturamento, desafio });
    setTimeout(() => { setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000); }, 500);
  };

  const hasChanges = email !== (lead.email || '') || porte !== (lead.porte || '') || tempo !== (lead.tempo_mercado || '') ||
    colaboradores !== (lead.num_colaboradores || '') || faturamento !== (lead.faturamento || '') ||
    desafio !== (lead.desafio || '');

  return (
    <div className="p-6 space-y-5">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-amber-700 text-sm font-semibold">Editar dados do lead</p>
        <p className="text-amber-600 text-xs mt-1">Estes campos foram removidos do formulário de registro para agilizá-lo. Preencha-os aqui.</p>
      </div>

      {/* E-mail */}
      <div>
        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">E-mail</p>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <p className="text-amber-700 text-sm font-semibold">Editar dados do lead</p>
        <p className="text-amber-600 text-xs mt-1">These fields were removed from the registration form for speed. Fill them here.</p>
      </div>

      {/* Porte */}
      <div>
        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Porte da empresa</p>
        <select value={porte} onChange={(e) => setPorte(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
          <option value="">Selecione...</option>
          <option value="MEI">MEI (até 1 colaborador)</option>
          <option value="Micro">Micro empresa (2-9 colaboradores)</option>
          <option value="Pequena">Pequena empresa (10-49 colaboradores)</option>
          <option value="Média">Média empresa (50-99 colaboradores)</option>
          <option value="Grande">Grande empresa (100+ colaboradores)</option>
        </select>
      </div>

      {/* Tempo no mercado */}
      <div>
        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Tempo no mercado</p>
        <select value={tempo} onChange={(e) => setTempo(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
          <option value="">Selecione...</option>
          <option value="Menos de 1 ano">Menos de 1 ano</option>
          <option value="1 a 3 anos">1 a 3 anos</option>
          <option value="3 a 5 anos">3 a 5 anos</option>
          <option value="5 a 10 anos">5 a 10 anos</option>
          <option value="Mais de 10 anos">Mais de 10 anos</option>
        </select>
      </div>

      {/* Número de colaboradores */}
      <div>
        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Número de colaboradores</p>
        <select value={colaboradores} onChange={(e) => setColaboradores(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
          <option value="">Selecione...</option>
          <option value="0">Nenhum (só eu)</option>
          <option value="1 a 5">1 a 5</option>
          <option value="6 a 10">6 a 10</option>
          <option value="11 a 25">11 a 25</option>
          <option value="26 a 50">26 a 50</option>
          <option value="51 a 100">51 a 100</option>
          <option value="100+">Mais de 100</option>
        </select>
      </div>

      {/* Faturamento mensal */}
      <div>
        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Faturamento mensal</p>
        <select value={faturamento} onChange={(e) => setFaturamento(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
          <option value="">Selecione...</option>
          <option value="Até R$ 5 mil">Até R$ 5 mil</option>
          <option value="R$ 5 a 20 mil">R$ 5 a 20 mil</option>
          <option value="R$ 20 a 50 mil">R$ 20 a 50 mil</option>
          <option value="R$ 50 a 100 mil">R$ 50 a 100 mil</option>
          <option value="R$ 100 a 500 mil">R$ 100 a 500 mil</option>
          <option value="R$ 500 mil a 1 mi">R$ 500 mil a 1 milhão</option>
          <option value="Acima de R$ 1 mi">Acima de R$ 1 milhão</option>
        </select>
      </div>

      {/* Desafio principal */}
      <div>
        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Principal desafio do negócio</p>
        <textarea value={desafio} onChange={(e) => setDesafio(e.target.value)} rows={3}
          placeholder="Ex: Conseguir clientes consistentes, organizar financeiro, estruturar equipe..."
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" />
      </div>

      {/* Save button */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all ${
            saved
              ? 'bg-emerald-500 text-white'
              : hasChanges
                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {saving ? (
            <><i className="ri-loader-4-line animate-spin" /> Salvando...</>
          ) : saved ? (
            <><i className="ri-check-line" /> Salvo!</>
          ) : (
            <><i className="ri-save-line" /> Salvar dados</>
          )}
        </button>
      </div>
    </div>
  );
}
